/**
 * Recognise ora's own notification mail, and which run it is about.
 *
 * Story 26738. THIS is the route into the pane that works today.
 *
 * WHY NOT THE CARD BUTTON
 * -----------------------
 * The Actionable Message card (see `initializationContext.ts`) is the supported
 * way to open the pane from inside an email, and it is built. It is not the
 * route this file replaces, though — it needs an Actionable Email originator id
 * (ADO 26722) before it renders for a recipient who is not the sender.
 *
 * CORRECTION 2026-09-24. An earlier version of this comment said Exchange
 * strips the `<script type="application/adaptivecard+json">` element in
 * transit, on the evidence that it was missing from the message read back
 * through Graph. That evidence was worthless and the conclusion was wrong:
 * Graph SANITISES `<script>` out of message bodies it returns, so a card that
 * renders perfectly looks identical through the API to one that never arrived.
 * The only valid test is to open the message in Outlook and look — done on
 * 2026-09-06, where the card rendered and its button opened the pane.
 *
 * Still open (ADO 28182): notifications are now WRITTEN into the Servicer's
 * mailbox rather than sent, so they carry no SPF/DKIM/ARC. Whether a card
 * survives that has not been measured. The header route below does not depend
 * on the answer.
 *
 * WHAT WORKS WITHOUT ANY OF THAT
 * ------------------------------
 * Every ora notification carries `X-Ora-Instance-Id`, which DOES survive
 * delivery — same verification, same messages. So with the pane pinned,
 * SELECTING the notification in the message list is enough: Outlook hands the
 * pane the new item, the pane reads the header, and it opens that run's
 * decision. No card, no registration, no admin.
 *
 * That is the same gesture the Servicer already uses for a lender request, and
 * it works in Outlook classic, new Outlook and OWA, all of which support pinning.
 *
 * THE HEADER NAME IS HALF OF A CROSS-REPO PAIR. The other half is
 * `workflows/src/workflows/notifications/headers.py`, which stamps it on the way
 * out. A disagreement of one character here is silent — the pane simply never
 * recognises an ora email — so the workflows repo pins this file's spelling in
 * `tests/contract/test_ora_header_sync.py`.
 */

/**
 * Lower-cased on purpose. RFC 5322 header names are case-insensitive, Exchange
 * echoes whatever the sender wrote, and it has been observed arriving fully
 * lower-cased.
 */
export const ORA_INSTANCE_HEADER = "x-ora-instance-id";

type HeaderCapableItem = {
  getAllInternetHeadersAsync?: (
    callback: (result: { status?: unknown; value?: unknown }) => void,
  ) => void;
};

/**
 * Pull the instance id out of a raw internet-header block.
 *
 * Exported because this is where the parsing risk lives: header blocks arrive
 * CRLF-delimited, folded across continuation lines, and in whatever case the
 * sending stack chose.
 */
export function parseOraInstanceId(headers: unknown): string {
  if (typeof headers !== "string" || !headers) return "";
  for (const rawLine of headers.split(/\r?\n/)) {
    // Folded continuation lines begin with whitespace and belong to the
    // PREVIOUS header, so they can never start a match. A UUID never folds in
    // practice; skipping them is simply correct rather than lossy.
    if (/^\s/.test(rawLine)) continue;
    const separator = rawLine.indexOf(":");
    if (separator < 0) continue;
    if (rawLine.slice(0, separator).trim().toLowerCase() !== ORA_INSTANCE_HEADER) {
      continue;
    }
    const value = rawLine.slice(separator + 1).trim();
    if (value) return value;
  }
  return "";
}

/**
 * The run the currently-open message is about, or "" when it is not ora's.
 *
 * Resolves "" for every ordinary email, which is almost all of them, and for
 * every host too old to expose the API (`getAllInternetHeadersAsync` is Mailbox
 * 1.8; the manifest's floor is 1.5). Never rejects: this runs inside the pane's
 * item read, and a rejection there would take the whole email context with it.
 *
 * IT RUNS ON EVERY MESSAGE THE SERVICER SELECTS, and there is no cheap way to
 * pre-filter: under the delegated transport ora's alert arrives with the
 * Servicer's own address in `from`, and the subject line is operator-editable,
 * so the header IS the only reliable signal. The cost is paid down instead: the
 * call runs in parallel with the two body reads, which are heavier, and it is
 * bounded so a slow online-mode mailbox delays nothing.
 */
export function readOraInstanceId(
  item: unknown,
  timeoutMs = 1500,
): Promise<string> {
  const capable = item as HeaderCapableItem | undefined;
  if (typeof capable?.getAllInternetHeadersAsync !== "function") {
    return Promise.resolve("");
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: string) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = setTimeout(() => finish(""), timeoutMs);
    try {
      capable.getAllInternetHeadersAsync!((result) => {
        clearTimeout(timer);
        finish(parseOraInstanceId(result?.value));
      });
    } catch {
      clearTimeout(timer);
      finish("");
    }
  });
}
